// src/features/tickets/hooks/useTickets.ts
//
// Hook fuer die Ticket-Listen-Ansicht (TicketsPage).
// Kapselt:
//   - Aktuelle Staff-ID-Resolution (user.email -> xtax_staff) inkl. der
//     gespeicherten Standardansicht (xtax_staff.ticket_standard_ansicht)
//     und der Liste der aktiven Mitarbeiter
//   - Filter-State: view, ausgewaehlter Mitarbeiter fuer die Ansicht "Meine"
//     (viewStaffId), showClosed, searchTerm
//   - Daten-Loading inkl. automatischer Re-Load bei Filter-Aenderung
//   - Seitenweises Laden (PAGE_SIZE) mit loadMore() und Gesamtzahl
//   - reload()-Funktion fuer manuelles Refresh (z.B. nach Anlage)
//
// Liste, Ansicht, Status und Suche liefert die Datenbank (RPC list_tickets_page
// ueber ticketService.listTicketsPage). Die Suche startet erst nach einer kurzen
// Tipp-Pause; Antworten veralteter Anfragen werden verworfen, damit ein
// langsameres Ergebnis fuer einen Teilbegriff nicht das aktuelle ueberschreibt.
//
// Ansicht "Meine" fuer andere Mitarbeiter: Ist viewStaffId gesetzt, laedt die
// Ansicht "Meine" die Liste dieses Mitarbeiters (dieselbe Definition wie fuer
// die eigene Liste, siehe DB-Funktion ticket_in_ansicht). Ohne Auswahl gilt der
// angemeldete Mitarbeiter. Die Auswahl wird nicht gespeichert; nach dem
// Neuladen der Seite gilt wieder die eigene Liste. Die Standardansicht
// betrifft weiterhin nur die Ansicht selbst (Meine, Pool, Alle).
//
// UI/Routing bleibt komplett in TicketsPage; dieser Hook ist reine Logik.

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../auth';
import { staffService } from '../../../services/staff/StaffService';
import { EmailSenderIdentity } from '../../../services/email/types';
import { ticketService } from '../services/TicketService';
import { supabase } from '../../../shared/services/supabase';
import {
  TicketListView,
  TicketPageFilter,
  TicketView
} from '../types';

/** Anzahl Tickets pro Seite bzw. pro "Weitere laden". */
const PAGE_SIZE = 100;

/** Obergrenze, die die Datenbank pro Abfrage liefert (siehe list_tickets_page). */
const MAX_ROWS_PER_REQUEST = 500;

/** Tipp-Pause in Millisekunden, bevor die Suche startet. */
const SEARCH_DEBOUNCE_MS = 300;

export interface UseTicketsResult {
  // Daten
  tickets: TicketListView[];
  /** true nur beim ersten Laden (noch keine Liste vorhanden). */
  loading: boolean;
  /** true, waehrend eine bestehende Liste neu geladen wird (Filter/Suche). */
  refreshing: boolean;
  /** true, waehrend "Weitere laden" laeuft. */
  loadingMore: boolean;
  /** Gesamtzahl der Tickets fuer Ansicht/Filter bzw. Suche. */
  totalCount: number;
  /** true, wenn weitere Tickets nachgeladen werden koennen. */
  hasMore: boolean;
  /** true, wenn das letzte Laden fehlgeschlagen ist. */
  loadError: boolean;
  /** true, wenn ein Suchbegriff aktiv ist (Suche ueber alle Tickets). */
  searchActive: boolean;
  currentStaffId: string | null;
  staffResolved: boolean;
  /** Aktive Mitarbeiter (Auswahl fuer die Ansicht "Meine"). */
  staffList: EmailSenderIdentity[];

  // Filter (controlled)
  view: TicketView;
  setView: (v: TicketView) => void;
  /**
   * Mitarbeiter, dessen Ansicht "Meine" gezeigt wird; null = angemeldeter
   * Mitarbeiter.
   */
  viewStaffId: string | null;
  setViewStaffId: (staffId: string | null) => void;
  showClosed: boolean;
  setShowClosed: (b: boolean) => void;
  searchTerm: string;
  setSearchTerm: (s: string) => void;

  // Standardansicht
  /** Gespeicherte Standardansicht des Mitarbeiters (null = unbekannt). */
  standardView: TicketView | null;
  savingStandardView: boolean;
  /** Speichert die aktuell gewaehlte Ansicht als Standardansicht. */
  saveStandardView: () => Promise<void>;

  // Aktionen
  reload: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export const useTickets = (): UseTicketsResult => {
  const { user } = useAuth();

  // Auth-Staff-Resolution
  const [currentStaffId, setCurrentStaffId] = useState<string | null>(null);
  const [staffResolved, setStaffResolved] = useState(false);
  const [staffList, setStaffList] = useState<EmailSenderIdentity[]>([]);

  // Filter-State
  const [view, setView] = useState<TicketView>('mine');
  const [viewStaffId, setViewStaffId] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Mitarbeiter, fuer den die Liste geladen wird: die Auswahl fuer "Meine",
  // sonst der angemeldete Mitarbeiter. Pool und Alle werten die ID nicht aus.
  const listStaffId = viewStaffId ?? currentStaffId;

  // Standardansicht
  const [standardView, setStandardView] = useState<TicketView | null>(null);
  const [savingStandardView, setSavingStandardView] = useState(false);

  // Daten-State
  const [tickets, setTickets] = useState<TicketListView[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Laufende Anfrage-Nummer: nur die Antwort der juengsten Anfrage wird
  // uebernommen. loadMore prueft gegen dieselbe Nummer und verwirft sein
  // Ergebnis, wenn zwischenzeitlich neu geladen wurde.
  const requestIdRef = useRef(0);
  const ticketsCountRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  useEffect(() => {
    ticketsCountRef.current = tickets.length;
  }, [tickets]);

  // Aktuellen Mitarbeiter ermitteln (gleiches Pattern wie DeadlineDashboard)
  // und dessen gespeicherte Standardansicht als Start-Ansicht setzen, bevor
  // das erste Laden startet (staffResolved gibt das Laden frei). Die Liste der
  // aktiven Mitarbeiter dient zugleich als Auswahl fuer die Ansicht "Meine".
  useEffect(() => {
    const resolveCurrentStaff = async () => {
      if (!user?.email) {
        setStaffResolved(true);
        return;
      }
      try {
        const allStaff = await staffService.getActiveStaff();
        setStaffList(allStaff);
        const me = allStaff.find(
          s => s.email.toLowerCase() === user.email.toLowerCase()
        );
        if (me) {
          setCurrentStaffId(me.id);
          const savedView = await ticketService.getStandardView(me.id);
          if (savedView) {
            setStandardView(savedView);
            setView(savedView);
          }
        }
      } catch (error) {
        console.error('[useTickets] Staff-Resolution-Error:', error);
      } finally {
        setStaffResolved(true);
      }
    };
    resolveCurrentStaff();
  }, [user?.email]);

  // Suchbegriff entprellen: erst nach einer Tipp-Pause suchen. Ein geleertes
  // Feld wirkt sofort.
  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed === '') {
      setDebouncedSearch('');
      return;
    }
    const timer = setTimeout(() => setDebouncedSearch(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Tickets laden (re-runs bei Filter-Aenderung)
  // silent=true (Realtime-Refetch): keine Ladeanzeige, laedt die bisher
  // angezeigte Menge neu und behaelt bei einem voruebergehenden Fehler die
  // vorhandene Liste.
  const reload = useCallback(async (silent = false) => {
    if (!staffResolved) return;

    const requestId = ++requestIdRef.current;
    const limit = silent
      ? Math.min(Math.max(PAGE_SIZE, ticketsCountRef.current), MAX_ROWS_PER_REQUEST)
      : PAGE_SIZE;

    if (!silent) {
      if (hasLoadedOnceRef.current) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
    }

    const filter: TicketPageFilter = {
      view,
      current_staff_id: listStaffId,
      include_closed: showClosed,
      search: debouncedSearch || undefined,
      limit,
      offset: 0
    };

    const result = await ticketService.listTicketsPage(filter);

    // Veraltete Antwort verwerfen
    if (requestId !== requestIdRef.current) return;

    if (result.success) {
      setTickets(result.tickets);
      setTotalCount(result.total_count);
      setLoadError(false);
      hasLoadedOnceRef.current = true;
    } else if (!silent || !hasLoadedOnceRef.current) {
      setTickets([]);
      setTotalCount(0);
      setLoadError(true);
    }

    // Die juengste Anfrage beendet jede Ladeanzeige, auch wenn sie eine stille
    // Live-Aktualisierung ist, die eine sichtbare Anfrage abgeloest hat.
    setLoading(false);
    setRefreshing(false);
  }, [view, listStaffId, showClosed, debouncedSearch, staffResolved]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Naechste Seite anhaengen
  const loadMore = useCallback(async () => {
    if (!staffResolved || loadingMore) return;

    const requestId = requestIdRef.current;
    setLoadingMore(true);

    const filter: TicketPageFilter = {
      view,
      current_staff_id: listStaffId,
      include_closed: showClosed,
      search: debouncedSearch || undefined,
      limit: PAGE_SIZE,
      offset: ticketsCountRef.current
    };

    const result = await ticketService.listTicketsPage(filter);

    // Zwischenzeitlich neu geladen (Filter, Suche, Live-Aktualisierung): verwerfen
    if (requestId !== requestIdRef.current) {
      setLoadingMore(false);
      return;
    }

    if (result.success) {
      setTickets(prev => {
        const known = new Set(prev.map(t => t.id));
        return [...prev, ...result.tickets.filter(t => !known.has(t.id))];
      });
      setTotalCount(result.total_count);
      setLoadError(false);
    } else {
      setLoadError(true);
    }

    setLoadingMore(false);
  }, [view, listStaffId, showClosed, debouncedSearch, staffResolved, loadingMore]);

  // Aktuelle Ansicht als Standardansicht speichern
  const saveStandardView = useCallback(async () => {
    if (savingStandardView) return;
    if (!currentStaffId) {
      alert('Die Standardansicht konnte nicht gespeichert werden: Dein Login ist keinem aktiven Mitarbeiter zugeordnet.');
      return;
    }
    setSavingStandardView(true);
    const result = await ticketService.setStandardView(currentStaffId, view);
    if (result.success) {
      setStandardView(view);
    } else {
      alert(`Die Standardansicht konnte nicht gespeichert werden: ${result.error ?? 'Unbekannter Fehler'}`);
    }
    setSavingStandardView(false);
  }, [view, currentStaffId, savingStandardView]);

  // reload-Ref haelt stets die aktuelle reload-Funktion (mit aktuellen Filtern),
  // damit das Realtime-Abo nicht bei jeder Filteraenderung neu aufgebaut wird.
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  // Live-Aktualisierung (F1): jede Aenderung an der tickets-Tabelle laedt die
  // Liste entprellt neu. Neue Nachrichten heben tickets.last_message_at (durch
  // die Inbound-Pipeline) und erscheinen so ueber das UPDATE-Event mit; ein Abo
  // auf tickets genuegt. Refetch statt Payload-Merge, damit die angereicherte
  // Listen-Form erhalten bleibt.
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const triggerReload = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => { reloadRef.current(true); }, 400);
    };
    const channel = supabase
      .channel('tickets-list-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tickets' }, triggerReload)
      .subscribe();
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    tickets,
    loading,
    refreshing,
    loadingMore,
    totalCount,
    hasMore: tickets.length < totalCount,
    loadError,
    searchActive: debouncedSearch !== '',
    currentStaffId,
    staffResolved,
    staffList,
    view,
    setView,
    viewStaffId,
    setViewStaffId,
    showClosed,
    setShowClosed,
    searchTerm,
    setSearchTerm,
    standardView,
    savingStandardView,
    saveStandardView,
    reload,
    loadMore
  };
};
